import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types'
export const TYPES = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'link', 'default']
export const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']
export const ICON_PLACEMENT = ['start', 'end', 'top', 'bottom']
const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const color = PropTypes.color
const Icon = PropTypes.Icon

const theme = {
  buttonColor: color,
  buttonHoverColor: color,
  buttonBorderColor: color,
  buttonBorderWidth: PropTypes.string,
  buttonBorderStyle: PropTypes.oneOf(BORDER_STYLES),
  buttonBorderRadius: PropTypes.string,
  buttonPadding: PropTypes.string,
  labelColor: color,
  labelHoverColor: color,
  fontSize: PropTypes.string
}
export default class Button extends Component {
  static propTypes = {
    /**
     * Button label text
     */
    label: PropTypes.string,

    /**
     * A picklist to choose a button-icon
     */
    icon: Icon,

    /**
     * Size of the button-icon; if selected, font-size won't affect the icon's size
     */
    iconSize: PropTypes.oneOf(ICON_SIZE),

    /**
     * Color of the button-icon
     */
    iconColor: color,

    /**
     * Button-icon placement
     */
    iconPlacement: PropTypes.oneOf(ICON_PLACEMENT),

    /**
     * Function to be invoked when the button is clicked
     */
    onClick: PropTypes.func,

    /**
     * Bootstrap 4 button types for "convenience"
     */
    type: PropTypes.oneOf(TYPES),

    /** Overall button-style: Is the button outlined, or solid. */
    outlined: PropTypes.bool,

    /** Adjust the colors, borders and alignment */
    styling: PropTypes.shape(theme),

    /** Set an aria label for the button */
    ariaLabel: PropTypes.string,

    /** Determines if icon is spinning */
    iconRotation: PropTypes.bool,

    /**
     * Loading spinner
     */
    isLoading: PropTypes.bool,

    /**
     * Loading icon
     */
    loadingIcon: Icon,

    /**
     * Size of the button-icon when loading; if selected, font-size won't affect the icon's size
     */
    loadingIconSize: PropTypes.oneOf(ICON_SIZE),

    /**
     * Color of the button-icon when loading
     */
    loadingIconColor: color,

    /**
     * Determines if icon is spinning
     * */
    loadingIconRotation: PropTypes.bool,

    /**
     * Button loading label text
     */
    loadingLabel: PropTypes.string,

    /**
     * Display loading dots animations
     * */
    loadingDotsAnimation: PropTypes.bool,

    /**
     * Type of dots animation
     */
    loadingDotsAnimationType:  PropTypes.oneOf(['dots-falling', 'dots-loading']),
    /** Enable / Disable the disabled state of the button. When disabled, the button cannot be interacted with. */
    disabled: PropTypes.bool
  }

  static defaultProps = {
    label: 'Button',
    type: 'secondary',
    outlined: false,
    disabled: false,
    iconPlacement: 'start',
    iconRotation: false,
    styling: {
      fontSize: '12px'
    }
  }

  toast = this.props.ctx && this.props.ctx.notify

  state = {
    disabled: false
  }

  onClick = async (e) => {
    const { onClick } = this.props
    if (!onClick) return
    try {
      this.setState({
        disabled: true,
        isLoading: true
      })

      await new Promise(resolve => resolve(onClick()))

      this.setState({
        disabled: false,
        isLoading: false
      })
    } catch (error) {
      if (this.toast) this.toast.error(error.toString())
    }
  }

  render () {
    const {
      type,
      styling: {
        fontSize,
        labelColor,
        labelHoverColor,
        buttonBorderColor,
        buttonHoverBorderColor,
        buttonDisabledBorderColor,
        buttonBorderWidth,
        buttonBorderStyle,
        buttonBorderRadius,
        buttonColor,
        buttonHoverColor,
        buttonDisabledColor,
        buttonPadding
      } = {},
      outlined,
      icon,
      iconColor,
      iconSize,
      iconPlacement,
      iconRotation,
      label,
      disabled,
      ariaLabel,
      style,
      className,
      isLoading,
      loadingIcon,
      loadingIconSize,
      loadingIconColor,
      loadingIconRotation,
      loadingDotsAnimationType,
      loadingLabel,
      loadingDotsAnimation
    } = this.props
    const iconStyle = (iconColor ? { color: `${iconColor}` } : {})
    const loadingIconStyle = (loadingIconColor ? { color: `${loadingIconColor}` } : {})
    return (
      <Fragment>
        <button
          className={
            `
            ${className || ``}
            btn
            ${(!!outlined && `btn-outline-${type}`) || `btn-${type}`}
            `
          }
          aria-label={ariaLabel}
          color={type}
          disabled={disabled || this.state.disabled}
          onClick={this.onClick}
          style={style}
        >
          {
            isLoading || this.state.isLoading
              ? <>
                {
                  loadingIcon &&
                  <i style={loadingIconStyle} className={`zmdi zmdi-${loadingIcon} zmdi-hc-${loadingIconRotation ? 'spin' : 'fw'} ${!!loadingIconSize && `zmdi-hc-${loadingIconSize}`}`} />
                }
                {
                  !loadingIcon &&
                  <div  className={'d-flex align-items-center'}>
                    <span className={`button-label`}>{loadingLabel || label}</span>
                    {
                      loadingDotsAnimation &&
                      <div className={loadingDotsAnimationType || 'dots-loading'} />
                    }
                  </div>
                }
              </>
              : <>
                {
                  icon &&
                  <i style={iconStyle} className={`zmdi zmdi-${icon} zmdi-hc-${iconRotation ? 'spin' : 'fw'} ${!!iconSize && `zmdi-hc-${iconSize}`}`} />
                }
                <span className={`button-label`}>{label}</span>
              </>
          }
          <style jsx>{`
          button {
            margin: 0 auto;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: ${iconPlacement === `start` || iconPlacement === `end` ? (iconPlacement === `start` && `row`) || `row-reverse` : (iconPlacement === `top` && `column`) || `column-reverse`};
          }

          button {
            padding: ${buttonPadding};
            background-color: ${buttonColor};
            border-color: ${buttonBorderColor};
            border-width: ${buttonBorderWidth};
            border-style: ${buttonBorderStyle};
            border-radius: ${buttonBorderRadius};
          }

          button:active {
            background-color: ${buttonColor};
            border-width: ${buttonBorderWidth};
            border-color: ${buttonBorderColor};
          }
          
          .btn:not(:disabled):not(.disabled):active {
            background-color: ${buttonColor};
            border-width: ${buttonBorderWidth};
            border-color: ${buttonBorderColor};
          }

          button:focus {
            box-shadow: none;
          }

          button:hover {
            background-color: ${buttonHoverColor};
            border-color: ${buttonHoverBorderColor};
          }
          button:disabled {
            background-color: ${buttonDisabledColor};
            border-color: ${buttonDisabledBorderColor};
          }
          button .button-label {
            vertical-align: middle;
            font-size: ${fontSize};
            ${labelColor ? `color: ${labelColor};` : ``}
          }

          button i {
            margin: 0 0.1em;
            vertical-align: middle;
            ${!iconSize ? `font-size: ${fontSize};` : ``}
            ${labelColor ? `color: ${labelColor};` : ``}
          }

          button:hover .button-label,
          button:hover i {
            ${labelHoverColor ? `color: ${labelHoverColor};` : ``}
          }
          
          @keyframes dotLoading {
            0%, 20% {
              color: rgba(0,0,0,0);
              text-shadow:
                .25em 0 0 rgba(0,0,0,0),
                .5em 0 0 rgba(0,0,0,0);
            }
            40% {
              color: white;
              text-shadow:
                .25em 0 0 rgba(0,0,0,0),
                .5em 0 0 rgba(0,0,0,0);
            }
            60% {
              text-shadow:
                .25em 0 0 white,
                .5em 0 0 rgba(0,0,0,0);
            }
            80%, 100% {
              text-shadow:
              .25em 0 0 white,
              .5em 0 0 white;
            }
          }
          
          @keyframes dotFalling {
            0% {
              box-shadow: 10006px -15px 0 0 rgba(152, 128, 255, 0);
            }
            25%,
            50%,
            75% {
              box-shadow: 10006px 3px 0 0 ${labelColor || '#fff'};
            }
            100% {
              box-shadow: 10006px 15px 0 0 rgba(152, 128, 255, 0);
            }
          }
          
          @keyframes dotFallingBefore {
            0% {
              box-shadow: 10000px -15px 0 0 rgba(152, 128, 255, 0);
            }
            25%,
            50%,
            75% {
              box-shadow: 10000px 3px 0 0 ${labelColor || '#fff'};
            }
            100% {
              box-shadow: 10000px 15px 0 0 rgba(152, 128, 255, 0);
            }
          }
          
          @keyframes dotFallingAfter {
            0% {
              box-shadow: 10010px -15px 0 0 rgba(152, 128, 255, 0);
            }
            25%,
            50%,
            75% {
              box-shadow: 10010px 3px 0 0 ${labelColor || '#fff'};
            }
            100% {
              box-shadow: 10010px 15px 0 0 rgba(152, 128, 255, 0);
            }
          }
          
          .dots-loading:after {
            content: ' .';
            animation: dotLoading 1s steps(5, end) infinite;
          }
          
          .dots-falling {
            position: relative;
            left: -9999px;
            width: 2px;
            height: 2px;
            border-radius: 2px;
            background-color: ${labelColor || '#fff'};
            color: ${labelColor || '#fff'};
            box-shadow: 10000px 3px 0 0 ${labelColor || '#fff'};
            animation: dotFalling 1s infinite linear;
            animation-delay: .1s;
          }
          
          .dots-falling::before, .dots-falling::after {
            content: '';
            display: inline-block;
            position: absolute;
            top: 0;
          }
          
          .dots-falling::before {
            width: 2px;
            height: 2px;
            border-radius: 2px;
            background-color: ${labelColor || '#fff'};
            color: ${labelColor || '#fff'};
            animation: dotFallingBefore 1s infinite linear;
            animation-delay: 0s;
          }
          
          .dots-falling::after {
            width: 2px;
            height: 2px;
            border-radius: 2px;
            background-color: ${labelColor || '#fff'};
            color: ${labelColor || '#fff'};
            animation: dotFallingAfter 1s infinite linear;
            animation-delay: .2s;
          }
        `}</style>
        </button>
      </Fragment>
    )
  }
}
